/* eslint-disable no-console */
import produce from 'immer';
import { head } from 'ramda';
import React, { createContext, Dispatch, useContext, useEffect, useReducer } from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';
import { useStorageState } from 'react-storage-hooks';

import { useAuth } from '../../../../contexts/AuthContext/AuthContext';
import { CONSTANTS } from '../../../../core/config/constants';
import { removeProperty } from '../../../../core/utils/object';
import { CompanySearchFilters } from '../../../../generated/model/models';
import { getFilterDefinitions } from '../../Companies/Filter/FilterLibrary/FilterDefinitions';
import {
  FilterProviderProps,
  FinancialDataContextActions,
  FinancialDataContextActionTypes,
  FinancialDataContextProps,
  LibraryFilterItem,
} from './FinancialDataContext.types';
import {
  appendParent,
  flattenFilters,
  mandatoryFilters,
  parseSearchRequest,
} from './financialDataContext.utils';

const FilterContext = createContext<FinancialDataContextProps | undefined>(undefined);
const FilterDispatchContext = createContext<Dispatch<FinancialDataContextActions> | undefined>(
  undefined,
);

const initialFilterState = (
  version: string,
  isRefinement: boolean,
  filters: LibraryFilterItem[],
): FinancialDataContextProps => ({
  data: {},
  activeFiltter: '',
  requestFiltersData: {},
  historySearch: true,
  libraryFilters: filters,
  flattenedLibraryFilters: flattenFilters(filters),
  expandedLibraryFilters: [],
  appliedFilters: [],
  expandedAppliedFilter: '',
  version: version,
  saveFilterButtonState: false,
  predefinedFilters: [],
  predefinedFirstRender: false,
  isRefinement: isRefinement,
  sortModel: undefined,
});

const FilterReducer = (state: FinancialDataContextProps, action: FinancialDataContextActions) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('ACTION SENT: ', action);
  }

  switch (action.type) {
    case FinancialDataContextActionTypes.Set: {
      return produce(state, (draft) => {
        const { historySearch, data, appliedFilters } = parseSearchRequest(
          action.payload,
          state.libraryFilters,
        );

        if (appliedFilters.length) {
          draft.historySearch = historySearch;
          draft.appliedFilters = appliedFilters;
          draft.activeFiltter = head(appliedFilters)!;
          draft.expandedAppliedFilter = head(appliedFilters)!;
          draft.data = data;
          draft.requestFiltersData = {};
          draft.expandedLibraryFilters = [];
        }
      });
    }
    case FinancialDataContextActionTypes.Add: {
      return produce(state, (draft) => {
        ReactGA.event({ category: 'FILTERS', action: 'ADD_FILTER', label: action.payload });
        const index = draft.appliedFilters.indexOf(action.payload);
        if (index === -1) draft.appliedFilters.push(action.payload);
        draft.activeFiltter = action.payload;
      });
    }
    case FinancialDataContextActionTypes.ToggleActiveFilter: {
      return produce(state, (draft) => {
        if (action.payload === draft.activeFiltter) {
          draft.activeFiltter = '';
          draft.expandedAppliedFilter = '';
        } else {
          draft.activeFiltter = action.payload;
          draft.expandedAppliedFilter = action.payload;
        }
      });
    }
    case FinancialDataContextActionTypes.Remove: {
      return produce(state, (draft) => {
        ReactGA.event({ category: 'FILTERS', action: 'REMOVE_FILTER', label: action.payload });
        // remove from the list
        const index = draft.appliedFilters.indexOf(action.payload);
        if (index !== -1) draft.appliedFilters.splice(index, 1);

        // delete defined filter data
        const updatedObject = removeProperty(action.payload);
        draft.data = updatedObject(draft.data);

        // expand first filter in the list if removed filter was expanded
        if (draft.expandedAppliedFilter === action.payload && draft.appliedFilters.length) {
          draft.expandedAppliedFilter = head(draft.appliedFilters)!;
        }

        // reset request filters data if there are no applied filters
        if (!draft.appliedFilters.length) {
          draft.requestFiltersData = {};
        }
      });
    }
    case FinancialDataContextActionTypes.ClearAppliedFilters: {
      return produce(state, (draft) => {
        ReactGA.event({ category: 'FILTERS', action: 'REMOVE_ALL_FILTERS' });
        const mandatoryOnly = mandatoryFilters(state.flattenedLibraryFilters);
        draft.appliedFilters = mandatoryOnly;
        if (mandatoryOnly.length) {
          draft.activeFiltter = head(mandatoryOnly)!;
          draft.expandedAppliedFilter = head(mandatoryOnly)!;
        }

        const dataset = draft.data.dataset;

        draft.requestFiltersData = {};
        draft.data = dataset ? { dataset: { value: dataset.value } } : {};
        draft.sortModel = undefined;
      });
    }
    case FinancialDataContextActionTypes.ToggleLibraryFilter: {
      return produce(state, (draft) => {
        const index = draft.expandedLibraryFilters.indexOf(action.payload);
        ReactGA.event({
          category: 'FILTERS',
          action: 'TOGGLE_FILTER_GROUP',
          label: action.payload,
        });

        if (index === -1) {
          draft.expandedLibraryFilters.push(action.payload);
        } else {
          draft.expandedLibraryFilters.splice(index, 1);
        }
      });
    }
    case FinancialDataContextActionTypes.ToggleAppliedFilter: {
      return produce(state, (draft) => {
        ReactGA.event({ category: 'FILTERS', action: 'TOGGLE_FILTER', label: action.payload });
        draft.expandedAppliedFilter =
          draft.expandedAppliedFilter === action.payload ? '' : action.payload;
      });
    }
    case FinancialDataContextActionTypes.UpdateFilterValues: {
      return produce(state, (draft) => {
        draft.data = { ...draft.data, ...action.payload };
      });
    }
    case FinancialDataContextActionTypes.AddIncludeKeywordsFilterValues: {
      return produce(state, (draft) => {
        draft.data = {
          ...draft.data,
          activityKeywords: {
            ...draft.data.activityKeywords,
            value: {
              tags: {
                excludeValues: draft.data.activityKeywords?.value?.tags?.excludeValues ?? [],
                includeValues:
                  draft.data.activityKeywords?.value?.tags?.includeValues &&
                  draft.data.activityKeywords?.value?.tags?.includeValues.length > 0
                    ? draft.data.activityKeywords?.value?.tags?.includeValues.indexOf(
                        action.payload,
                      ) >= 0
                      ? [...draft.data.activityKeywords.value.tags.includeValues]
                      : [...draft.data.activityKeywords.value.tags.includeValues, action.payload]
                    : [action.payload],
              },
            },
          },
        };
      });
    }
    case FinancialDataContextActionTypes.AddExcludeKeywordsFilterValues: {
      return produce(state, (draft) => {
        draft.data = {
          ...draft.data,
          activityKeywords: {
            ...draft.data.activityKeywords,
            value: {
              tags: {
                includeValues: draft.data.activityKeywords?.value?.tags?.includeValues ?? [],
                excludeValues:
                  draft.data.activityKeywords?.value?.tags?.excludeValues &&
                  draft.data.activityKeywords?.value?.tags?.excludeValues.length > 0
                    ? draft.data.activityKeywords?.value?.tags?.excludeValues.indexOf(
                        action.payload,
                      ) >= 0
                      ? [...draft.data.activityKeywords.value.tags.excludeValues]
                      : [...draft.data.activityKeywords.value.tags.excludeValues, action.payload]
                    : [action.payload],
              },
            },
          },
        };
      });
    }
    case FinancialDataContextActionTypes.UpdateHistorySearchValue: {
      return produce(state, (draft) => {
        draft.historySearch = action.payload;
      });
    }
    case FinancialDataContextActionTypes.SetRequestFilterValues: {
      return produce(state, (draft) => {
        draft.requestFiltersData = action.payload;
      });
    }
    case FinancialDataContextActionTypes.SetSaveFilterButtonState: {
      return produce(state, (draft) => {
        draft.saveFilterButtonState = action.payload;
      });
    }
    case FinancialDataContextActionTypes.SetPredefinedFilters: {
      return produce(state, (draft) => {
        draft.predefinedFilters = action.payload;
      });
    }
    case FinancialDataContextActionTypes.SetPredefinedFirstRender: {
      return produce(state, (draft) => {
        draft.predefinedFirstRender = action.payload;
      });
    }
    case FinancialDataContextActionTypes.SetSortModel: {
      return produce(state, (draft) => {
        draft.sortModel = action.payload;
      });
    }
    case FinancialDataContextActionTypes.ResetSortModel: {
      return produce(state, (draft) => {
        draft.sortModel = undefined;
      });
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};

export const FilterContextProvider = ({ children, storageKey, version }: FilterProviderProps) => {
  const { user } = useAuth();

  const isRefinement = version === CONSTANTS.REFINEMENT_COMPANIES_FILTER_STORAGE.VERSION;
  const filters = appendParent(getFilterDefinitions(user));

  // don't save to storage libraryFilters and flattenedLibraryFilters
  const { libraryFilters, flattenedLibraryFilters, ...rest } = initialFilterState(
    version,
    isRefinement,
    filters,
  );
  const [storageState, setStorageState] = useStorageState(sessionStorage, storageKey, rest);
  const [state, dispatch] = useReducer(FilterReducer, {
    ...(storageState.version === version ? storageState : rest),
    libraryFilters,
    flattenedLibraryFilters,
  });

  useEffect(() => {
    // don't save to storage libraryFilters and flattenedLibraryFilters
    // eslint-disable-next-line
    const { libraryFilters, flattenedLibraryFilters, ...rest } = state;
    setStorageState(rest);
  }, [state, setStorageState]);

  return (
    <FilterContext.Provider value={state}>
      <FilterDispatchContext.Provider value={dispatch}>{children}</FilterDispatchContext.Provider>
    </FilterContext.Provider>
  );
};

export const useFilterState = () => {
  const context = useContext(FilterContext);
  const { t } = useTranslation();

  if (context === undefined) {
    throw new Error(
      t('app:error.contextWithin', { action: 'FilterState', provider: 'FilterProvider' }),
    );
  }

  return context;
};

export function useFilterFieldState<T>(field: string | undefined): T | null {
  const state = useFilterState();

  return field ? ((state.data[field as keyof CompanySearchFilters] as unknown) as T) : null;
}

export const useFilterDispatch = () => {
  const context = useContext(FilterDispatchContext);
  const { t } = useTranslation();

  if (context === undefined) {
    throw new Error(
      t('app:error.contextWithin', { action: 'FilterDispatch', provider: 'FilterProvider' }),
    );
  }

  return context;
};
